import { REPORTS_ENDPOINTS } from "../constants/api";
import { privateApiClient } from "../utils/axios.utils";

/**
 * Reports Service
 * This service handles all API calls related to reports.
 */
export const ReportsService = {
  /**
   * Fetch combined reports with filtering and pagination
   */
  getCombinedReports: async (params?: {
    category?: "paid" | "unpaid" | "incomplete" | "all";
    page?: number;
    per_page?: number;
    examTypeIds?: string;
    examCategoryIds?: string;
    subjectIds?: string;
  }) => {
    const response = await privateApiClient.get(REPORTS_ENDPOINTS.COMBINED, {
      params,
      timeout: 60000, // 60 seconds — large per_page values can take longer
    });
    return response.data;
  },

  /**
   * Export combined reports to Excel/CSV
   */
  exportCombinedReports: async (params?: {
    category?: "paid" | "unpaid" | "incomplete" | "all";
    examTypeIds?: string;
    examCategoryIds?: string;
    subjectIds?: string;
  }) => {
    const response = await privateApiClient.get(REPORTS_ENDPOINTS.COMBINED_EXPORT, {
      params,
      responseType: "blob",
      timeout: 300000, // 5 minutes — export can be large for thousands of records
    });
    return response.data;
  },

  /**
   * Fetch school reports with pagination
   */
  getSchoolsReports: async (params?: {
    category?: "paid" | "unpaid" | "incomplete" | "all";
    page?: number;
    per_page?: number;
  }) => {
    const response = await privateApiClient.get(
      REPORTS_ENDPOINTS.SCHOOLS_REPORTS,
      {
        params,
      }
    );
    return response.data;
  },

  /**
   * Fetch total number of students registered for a specific subject
   */
  getSubjectStudentCount: async (subjectId: number) => {
    const response = await privateApiClient.get(
      REPORTS_ENDPOINTS.SUBJECT_STUDENTS(subjectId)
    );
    return response.data;
  },

  /**
   * Fetch exam type for a specific subject
   */
  getSubjectExamType: async (subjectId: number) => {
    const response = await privateApiClient.get(
      REPORTS_ENDPOINTS.SUBJECT_EXAM_TYPE(subjectId)
    );
    return response.data;
  },

  /**
   * Fetch school code from exam registrations
   */
  getSchoolCode: async (schoolId: number) => {
    const response = await privateApiClient.get(
      `/reports/schools/${schoolId}/code`
    );
    return response.data;
  },

  /**
   * Fetch school exam reports with filtering
   */
  getSchoolExamReports: async (params: {
    exam_type_ids: number[];
    school_id?: number;
    registration_status?: "paid" | "unpaid" | "incomplete" | "all";
    payment_status?: "paid" | "unpaid" | "all";
    page?: number;
    per_page?: number;
  }) => {
    const response = await privateApiClient.get(
      REPORTS_ENDPOINTS.SCHOOL_EXAM_REPORTS,
      {
        params,
      }
    );

    return response.data.data.data[0];
  },

  /**
   * Fetch exam types registered by a specific school
   */
  getSchoolExamTypes: async (schoolId: number) => {
    const response = await privateApiClient.get(
      REPORTS_ENDPOINTS.SCHOOL_EXAM_TYPES(schoolId)
    );

    return response.data;
  },
  /**
   * Download school exam report PDF
   */
  downloadSchoolExamReportPdf: async (params: {
    exam_type_ids: number[];
    school_id?: number;
  }) => {
    const response = await privateApiClient.get(
      REPORTS_ENDPOINTS.SCHOOL_EXAM_REPORTS + "/download-pdf",
      {
        params,
        responseType: "blob",
      }
    );
    return response.data;
  },
  /**
   * Download admin exam receipt PDF
   */
  downloadAdminExamReceiptPdf: async (params: {
    exam_type_ids: number[];
    school_id: number;
  }) => {
    const response = await privateApiClient.get(
      "/reports/admin/exam-receipts/download-pdf",
      {
        params,
        responseType: "blob",
      }
    );
    return response.data;
  },

  /**
   * Get schools that have registered students for given subjects with optional category filtering
   */
  getSchoolsBySubjects: async (subjectIds: number[], category?: string) => {
    const response = await privateApiClient.get(
      REPORTS_ENDPOINTS.SCHOOLS_BY_SUBJECTS,
      {
        params: { subject_ids: subjectIds, category },
        timeout: 300000, // 5 minutes — this endpoint can be slow for large datasets
      }
    );
    return response.data;
  },
};
